import { deepmerge } from "@fastify/deepmerge";
import { colors } from "../colors";
const mergeUpdates = deepmerge();
export var FormStatus;
(function (FormStatus) {
    FormStatus["Draft"] = "draft";
    FormStatus["InProgress"] = "in_progress";
    FormStatus["Completed"] = "completed";
    FormStatus["Finalized"] = "finalized";
})(FormStatus || (FormStatus = {}));
export function formStatusColor(status) {
    switch (status) {
        case FormStatus.Draft:
            return colors.grey;
        case FormStatus.InProgress:
            return colors.teal;
        case FormStatus.Completed:
            return colors.purple;
        case FormStatus.Finalized:
            return colors.green;
    }
}
export function formStatusLabel(status) {
    switch (status) {
        case FormStatus.Draft:
            return "Entwurf";
        case FormStatus.InProgress:
            return "In Bearbeitung";
        case FormStatus.Completed:
            return "Ausgefüllt";
        case FormStatus.Finalized:
            return "Fertiggestellt";
    }
}
function normalizeEntityPathPrefix(pathPrefix) {
    if (pathPrefix && !pathPrefix.endsWith(".")) {
        pathPrefix += ".";
    }
    return pathPrefix;
}
/**
 * Maps an array of form fields to an update object structure, considering a path prefix.
 * Handles both regular value fields and 'table' type fields (array of rows of fields).
 *
 * @param {FormField[]} fields - The array of form fields to process.
 * @param {string} pathPrefix - The prefix to remove from the 'mapsTo' property for each field.
 * @returns {Record<string, unknown>} - An object representing the updates mapped from the fields.
 */
export function mapFormFieldsToObject(fields, pathPrefix) {
    let updates = {};
    pathPrefix = normalizeEntityPathPrefix(pathPrefix);
    for (const field of fields) {
        if (!field.mapsTo) {
            continue;
        }
        // Use mapSingleFormField for all field types (regular values and table data)
        const fieldUpdate = mapSingleFormField(field, pathPrefix);
        updates = mergeUpdates(updates, fieldUpdate);
    }
    return updates;
}
/**
 * Maps a single form field to an update object or direct value, taking into account possible
 * nested paths specified by the 'mapsTo' property, and removing a given prefix from the path.
 *
 * If 'mapsTo' is not present, returns a simple object mapping field name to value.
 * If 'mapsTo' represents a nested property (e.g. 'bankDetails.iban'), constructs
 * nested objects as needed.
 * For table fields with data property, converts rows to an array of objects.
 *
 * @param {FormField} field - The field to map.
 * @param {string} pathPrefix - The prefix to remove from the 'mapsTo' property.
 * @returns {Record<string, unknown>} - An object representing this field mapped to its proper property.
 */
export function mapSingleFormField(field, pathPrefix) {
    // If the field has no mapsTo, return the name:value pair
    if (!field.mapsTo) {
        return { [field.name]: field.value };
    }
    // Skip fields that don't start with the expected prefix
    if (!field.mapsTo.startsWith(pathPrefix)) {
        return {};
    }
    const path = field.mapsTo.replace(pathPrefix, "");
    const pathParts = path.split(".");
    // Determine the value to set
    let valueToSet;
    // Check if this is a table field with data
    if (field.data && field.data.length > 0) {
        // Handle table data - convert rows to array of objects
        const tableData = [];
        for (const row of field.data) {
            const rowObject = {};
            for (const rowField of row) {
                if (rowField.name && "value" in rowField) {
                    rowObject[rowField.name] = rowField.value;
                }
            }
            tableData.push(rowObject);
        }
        valueToSet = tableData;
    }
    else if ("value" in field) {
        valueToSet = fieldValueNotEmpty(field.value) ? field.value : undefined;
    }
    else {
        // Field has neither value nor data, skip it
        return {};
    }
    if (pathParts.length === 1) {
        // Direct property mapping
        return { [path]: valueToSet };
    }
    else {
        // Nested property mapping - need to create intermediate objects
        let current = {};
        const root = current;
        for (let i = 0; i < pathParts.length - 1; i++) {
            const part = pathParts[i];
            if (!current[part] || typeof current[part] !== "object") {
                current[part] = {};
            }
            current = current[part];
        }
        current[pathParts[pathParts.length - 1]] = valueToSet;
        return { [pathParts[0]]: root[pathParts[0]] };
    }
}
/**
 * Checks if the specified condition is met for the provided form fields.
 *
 * If no condition is provided, returns true.
 * If the condition contains an array of field names, checks if any of the fields have the specified value(s).
 * If the condition contains a single field, checks if that field has the specified value(s).
 *
 * @param {FormField[]} fields - The array of form fields to evaluate.
 * @param {FormCondition | undefined} condition - The condition to check against the form fields.
 * @returns {boolean} True if the condition is met, false otherwise.
 */
export function checkCondition(fields, condition) {
    if (!condition) {
        return true;
    }
    if (Array.isArray(condition.field)) {
        return condition.field.some((field) => checkConditionOnSingleField(fields.find((f) => f.name === field), { ...condition, field }));
    }
    else {
        return checkConditionOnSingleField(fields.find((f) => f.name === condition.field), condition);
    }
}
/**
 * Checks if a single field satisfies the given condition.
 *
 * If the condition value is an array, returns true if the field's value is included in the array.
 * Otherwise, returns true if the field's value equals the condition value.
 *
 * @param {FormField | undefined} field - The form field to evaluate.
 * @param {FormCondition} condition - The condition to check against the field.
 * @returns {boolean} True if the field satisfies the condition, false otherwise.
 */
export function checkConditionOnSingleField(field, condition) {
    if (!field || typeof field.value === "undefined") {
        return false;
    }
    if (Array.isArray(condition.value)) {
        const includes = condition.value.includes(field.value);
        return includes;
    }
    const isEqual = field.value === condition.value;
    return isEqual;
}
/**
 * Returns the array of fields that should be rendered for the given form, taking into account
 * all section and field conditions.
 *
 * A section is included if its condition (if any) is met according to the form's current field values.
 * A field is included if its condition (if any) is met according to the form's current field values.
 *
 * @param {Form} form - The Form instance containing sections and fields.
 * @returns {FormField[]} Array of fields that are rendered (conditions satisfied).
 */
export function getRenderedFields(form) {
    const relevantSections = form.sections.filter((section) => checkCondition(form.fields, section.condition));
    const relevantFields = relevantSections
        .flatMap((section) => section.fields)
        .filter((field) => checkCondition(form.fields, field.condition));
    return relevantFields;
}
/**
 * Checks if the value of a field differs from the corresponding value in the current data object.
 *
 * This function compares the field's value to the value currently present in the supplied data
 * object, accounting for an optional path prefix in the field's 'mapsTo' property.
 *
 * @template T - The type of the current data object (e.g., EmployeePayrollData, etc.).
 * @param {FormField} field - The form field whose value should be compared.
 * @param {T} currentData - The object containing the current values to compare against.
 * @param {string} pathPrefix - The prefix to strip from the 'mapsTo' property before looking up the value in currentData.
 * @returns {boolean} True if the value in the field is different from the value in the current data object, false otherwise.
 */
export function fieldValueChanged(field, currentData, pathPrefix) {
    pathPrefix = normalizeEntityPathPrefix(pathPrefix);
    if (!field.mapsTo || !field.mapsTo.startsWith(pathPrefix)) {
        return false;
    }
    // Obtain the property path, with the prefix removed if present
    const path = pathPrefix ? field.mapsTo.replace(pathPrefix, "") : field.mapsTo;
    const currentValue = currentData[path];
    return currentValue !== field.value;
}
export function fieldValueNotEmpty(value) {
    return value !== undefined && value !== null && value !== "";
}
/**
 * Returns the option matching the field's current value, or undefined if none matches.
 *
 * Callers deciding between "not filled in" and "selected" must attempt this match BEFORE
 * any emptiness check: an empty string can be a legitimate option value (e.g. the
 * Konfession option "Keine"), so an empty field value only means "not filled in" when no
 * option matches it.
 */
export function findSelectedOption(field) {
    return field.options?.find((option) => option.value === field.value);
}
//# sourceMappingURL=form.util.js.map